import { useEffect } from "react";

interface SEOHeadProps {
  title?: string;
  description?: string;
  canonical?: string;
  ogType?: "website" | "article";
  ogImage?: string;
  article?: {
    headline: string;
    excerpt: string;
    author: string;
    publishedAt: string;
    updatedAt?: string;
    slug: string;
  };
}

const SITE_NAME = "ForexWireDaily";
const SITE_URL = "https://forexwiredaily.com";
const DEFAULT_TITLE = "ForexWireDaily — The Pulse of Forex, Fintech & Prop Trading";
const DEFAULT_DESC = "Breaking forex news, fintech innovations, prop firm exposés, broker reviews, and regulatory intelligence. Trusted by traders worldwide.";
const DEFAULT_OG_IMAGE = "/og-default.png";

function truncate(str: string, max: number): string {
  if (str.length <= max) return str;
  return str.slice(0, max - 3).trimEnd() + "...";
}

const SEOHead = ({
  title,
  description,
  canonical,
  ogType = "website",
  ogImage,
  article,
}: SEOHeadProps) => {
  useEffect(() => {
    // Build final values
    const pageTitle = article
      ? truncate(`${article.headline} - ${SITE_NAME}`, 60)
      : title
        ? truncate(`${title} - ${SITE_NAME}`, 60)
        : DEFAULT_TITLE;

    const pageDesc = article
      ? truncate(article.excerpt, 155)
      : description || DEFAULT_DESC;

    const pageCanonical = article
      ? `${SITE_URL}/news/${article.slug}`
      : canonical || window.location.href.split("?")[0];

    const pageImage = ogImage || DEFAULT_OG_IMAGE;

    // Set document title
    document.title = pageTitle;

    // Helper to set/create meta tags
    const setMeta = (attr: string, key: string, content: string) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    // Standard meta
    setMeta("name", "description", pageDesc);

    // Open Graph
    setMeta("property", "og:title", pageTitle);
    setMeta("property", "og:description", pageDesc);
    setMeta("property", "og:url", pageCanonical);
    setMeta("property", "og:type", ogType);
    setMeta("property", "og:site_name", "ForexWireDaily — Forex, Fintech & Prop Trading News");
    setMeta("property", "og:image", pageImage);

    // Twitter
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", pageTitle);
    setMeta("name", "twitter:description", pageDesc);
    setMeta("name", "twitter:image", pageImage);

    // Canonical link
    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", pageCanonical);
  }, [title, description, canonical, ogType, ogImage, article]);

  return null;
};

export default SEOHead;
